import axios from 'axios';
import type { AxiosResponse, GenericAbortSignal, InternalAxiosRequestConfig } from 'axios';
import localStorageStore from '../store/localStorageStore';
import { announceAccessRefused, announcePermissionsChanged, rememberSessionReturnPath } from '../api/session';
import 'nprogress/nprogress.css';

export const backendApi = import.meta.env.VITE_APP_ENDPOINT;

export const V2_COOKIE_AUTH = import.meta.env.VITE_V2_COOKIE_AUTH !== 'false';

const AUTH_EXPIRED_CODES = new Set([
  'ACCESS_TOKEN_EXPIRED',
  'PERMISSIONS_CHANGED',
  'NO_TOKEN',
  'INVALID_TOKEN',
]);

const PERMISSIONS_CHANGED = 'PERMISSIONS_CHANGED';

export const REFRESH_LOCK = 'dp-auth-refresh';
export const REFRESH_TIMEOUT_MS = 10_000;

export function isExpiredAuth(status?: number, data?: unknown): boolean {
  return (
    status === 401 &&
    !!data &&
    typeof data === 'object' &&
    AUTH_EXPIRED_CODES.has((data as { code?: string }).code as string)
  );
}

const codeOf = (data: unknown): unknown => (data && typeof data === 'object' ? (data as { code?: unknown }).code : undefined);

const isExpiredAuthError = (error: unknown): boolean =>
  axios.isAxiosError(error) && isExpiredAuth(error.response?.status, error.response?.data);

interface RecoverableConfig extends InternalAxiosRequestConfig {
  _v2Retried?: boolean;
  _v2Probe?: boolean;
  _v2Generation?: number;
}

type Renewal =
  | { readonly kind: 'renewed' }
  | { readonly kind: 'answered'; readonly response: AxiosResponse }
  | { readonly kind: 'failed' };

const RENEWED: Renewal = { kind: 'renewed' };
const FAILED: Renewal = { kind: 'failed' };

let sessionGeneration = 0;

const renewedSince = (config: RecoverableConfig): boolean =>
  typeof config._v2Generation === 'number' && config._v2Generation < sessionGeneration;

const refreshSession = (): Promise<boolean> =>
  axios.post(`${backendApi}/v2/auth/refresh`, {}, { withCredentials: true, timeout: REFRESH_TIMEOUT_MS }).then(
    () => true,
    () => false,
  );

let tabQueue: Promise<unknown> = Promise.resolve();

const inTabLock = <T>(work: () => Promise<T>): Promise<T> => {
  const turn = tabQueue.then(work, work);
  tabQueue = turn.then(
    () => undefined,
    () => undefined,
  );
  return turn;
};

const webLocks = (): LockManager | null =>
  typeof navigator !== 'undefined' && 'locks' in navigator && typeof navigator.locks?.request === 'function'
    ? navigator.locks
    : null;

const abortSignalOf = (signal: GenericAbortSignal | undefined): AbortSignal | undefined =>
  typeof AbortSignal !== 'undefined' && signal instanceof AbortSignal ? signal : undefined;

const underRefreshLock = <T>(work: () => Promise<T>, signal: AbortSignal | undefined): Promise<T> => {
  const locks = webLocks();
  return locks === null ? inTabLock(work) : locks.request(REFRESH_LOCK, { signal }, work).then((result) => result);
};

const untilAborted = async <T>(work: Promise<T>, signal: GenericAbortSignal | undefined): Promise<T> => {
  if (!signal) return work;
  let stop = (): void => undefined;
  const aborted = new Promise<never>((_resolve, reject) => {
    stop = () => reject(new axios.CanceledError());
    if (signal.aborted) stop();
    else signal.addEventListener?.('abort', stop);
  });
  try {
    return await Promise.race([work, aborted]);
  } finally {
    signal.removeEventListener?.('abort', stop);
  }
};

/**
 * Normalise an axios error into the interceptor's rejection: the legacy
 * `{ open, type, message, status }` shape every existing caller already relies on,
 * PLUS the v2 error envelope (`code`, `violations`, and the compare-and-swap
 * `expectedVersion`/`currentVersion`) whenever the backend sends them. The extra
 * keys are purely additive — legacy callers ignore them; v2 screens read them to
 * explain a rejection (a rule violation, a stale version). Never dereferences a
 * missing `response`, so a response-less error (timeout, abort, CORS) can't crash.
 */
export function normalizeAxiosError(error: any, fallbackMessage?: string) {
  const data = error && error.response && typeof error.response.data === 'object' ? error.response.data : null;
  const rejection: any = {
    open: true,
    type: 'error',
    status: error && error.response ? error.response.status : undefined,
    message:
      (data && (data.error || data.message)) || fallbackMessage || (error && error.message) || 'Something went wrong.',
  };
  if (data) {
    if (data.code != null) rejection.code = data.code;
    if (Array.isArray(data.violations)) rejection.violations = data.violations;
    if (data.expectedVersion != null) rejection.expectedVersion = data.expectedVersion;
    if (data.currentVersion != null) rejection.currentVersion = data.currentVersion;
  }
  return rejection;
}

export default function axiosInstance(history: any = null, passedHeaders: any = null) {
  let headers: any = passedHeaders ? passedHeaders : {};

  // Auth is carried by the httpOnly access cookie via `withCredentials` (below);
  // no Authorization header and no token in localStorage.

  const entityId = localStorage.getItem('selectedEntity');
  if (entityId) {
    headers.entity = entityId;
  }

  const axiosInstance = axios.create({
    baseURL: backendApi,
    headers,
    // Send cookies to the API and honour its Set-Cookie responses (v2 httpOnly
    // access/refresh cookies). Requires the backend to send
    // `Access-Control-Allow-Credentials: true` with a specific
    // `Access-Control-Allow-Origin` (never `*`) or the browser drops the cookie.
    withCredentials: true,
  });

  function clearTokenAndRedirectToHome() {
    rememberSessionReturnPath();
    localStorageStore.logout();
    if (history) {
      history.replace('/');
    } else {
      window.location.replace('/');
    }
  }

  async function probe(config: RecoverableConfig): Promise<AxiosResponse | null> {
    const probeConfig: RecoverableConfig = { ...config, _v2Probe: true };
    try {
      const response = await axiosInstance.request(probeConfig);
      return isExpiredAuth(response.status, response.data) ? null : response;
    } catch (error) {
      if (isExpiredAuthError(error)) return null;
      throw error;
    }
  }

  async function renewUnderLock(config: RecoverableConfig): Promise<Renewal> {
    if (renewedSince(config)) return RENEWED;
    const answer = await probe(config);
    if (answer !== null) return { kind: 'answered', response: answer };
    if (!(await refreshSession())) return FAILED;
    sessionGeneration += 1;
    return RENEWED;
  }

  async function recoverExpiredAuth(config: RecoverableConfig | undefined, data: unknown): Promise<AxiosResponse | null> {
    if (!V2_COOKIE_AUTH || !config || config._v2Retried) return null;
    config._v2Retried = true;
    const renewal = renewedSince(config)
      ? RENEWED
      : await untilAborted(
          underRefreshLock(() => renewUnderLock(config), abortSignalOf(config.signal)),
          config.signal,
        );
    if (renewal.kind === 'failed') return null;
    if (codeOf(data) === PERMISSIONS_CHANGED) announcePermissionsChanged();
    return renewal.kind === 'answered' ? renewal.response : axiosInstance.request(config);
  }

  axiosInstance.interceptors.request.use((config: RecoverableConfig) => {
    config._v2Generation = sessionGeneration;
    return config;
  });

  axiosInstance.interceptors.response.use(
    async response => {
      const config: RecoverableConfig = response.config;
      if (!config._v2Probe && isExpiredAuth(response.status, response.data)) {
        const recovered = await recoverExpiredAuth(config, response.data);
        if (recovered) return recovered;
      }
      if (response.status === 403) announceAccessRefused(config.url, response.data);
      return response;
    },
    async error => {
      if (axios.isCancel(error)) {
        return Promise.reject(error);
      }
      // Handle specific errors and gracefully handle them
      if (
        error.request?.responseType === 'blob' &&
        typeof error.response?.data?.type === 'string' &&
        error.response.data.type.toLowerCase().includes('json')
      ) {
        return new Promise(async (resolve, reject) => {
          const bufferArray = await error.response.data.text();
          const err = JSON.parse(bufferArray);
          reject({ open: true, type: 'error', message: err.error });
        });
      }
      if (error.message === 'Network Error') {
        return new Promise((resolve, reject) => {
          reject({ open: true, type: 'error', status: error?.response?.status });
        });
      }

      // Response-less error (timeout, abort, CORS). Previously dereferenced
      // error.response.data here and threw — normalizeAxiosError is null-safe.
      if (!error.response) {
        return Promise.reject(normalizeAxiosError(error));
      }

      if (error.response.status === 403) announceAccessRefused(error.config?.url, error.response.data);

      if (error.response.status === 401) {
        const config: RecoverableConfig | undefined = error.config;
        if (isExpiredAuth(error.response.status, error.response.data)) {
          if (config?._v2Probe) return Promise.reject(error);
          const recovered = await recoverExpiredAuth(config, error.response.data);
          if (recovered) return recovered;
        }
        clearTokenAndRedirectToHome();
        return Promise.reject(normalizeAxiosError(error, 'Unauthorized access.'));
      }

      // Carries status + the v2 envelope (code/violations/versions) when present.
      return Promise.reject(normalizeAxiosError(error));
    }
  );

  return axiosInstance;
}
